/**
 * Crash-safe storage for QA sessions in IndexedDB: the manifest, and the recording in chunks
 * written as they arrive (events, levels, microphone audio, utterances). A session survives a
 * reload or crash and is assembled into files on stop or on the next visit.
 */
import type { QaManifest } from './events';

export type ChunkKind = 'events' | 'levels' | 'mic' | 'utterance';

export interface Chunk {
  session: string;
  seq: number;
  kind: ChunkKind;
  /** Text (JSON lines) for events and levels; samples for audio. */
  text?: string;
  audio?: Float32Array;
  /** Utterance number (for utterance chunks). */
  utterance?: number;
}

export interface StoredSession {
  manifest: QaManifest;
  /** recording | stopped (ready to upload) | uploaded */
  state: 'recording' | 'stopped' | 'uploaded';
}

export interface QaStore {
  putSession(session: StoredSession): Promise<void>;
  sessions(): Promise<StoredSession[]>;
  putChunk(chunk: Chunk): Promise<void>;
  chunks(session: string): Promise<Chunk[]>;
  deleteSession(session: string): Promise<void>;
}

const DB = 'foe-names-qa';

function request<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => {
      resolve(r.result);
    };
    r.onerror = () => {
      reject(r.error ?? new Error('IndexedDB request failed'));
    };
  });
}

/** The browser's IndexedDB store. */
export function indexedDbStore(): QaStore {
  let db: Promise<IDBDatabase> | undefined;
  const open = () =>
    (db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => {
        r.result.createObjectStore('sessions', { keyPath: 'manifest.id' });
        const chunks = r.result.createObjectStore('chunks', { keyPath: ['session', 'seq'] });
        chunks.createIndex('session', 'session');
      };
      r.onsuccess = () => {
        resolve(r.result);
      };
      r.onerror = () => {
        reject(r.error ?? new Error('could not open IndexedDB'));
      };
    }));
  const store = async (name: string, mode: IDBTransactionMode) =>
    (await open()).transaction(name, mode).objectStore(name);
  return {
    putSession: async (s) => {
      await request((await store('sessions', 'readwrite')).put(s));
    },
    sessions: async () => request((await store('sessions', 'readonly')).getAll()),
    putChunk: async (c) => {
      await request((await store('chunks', 'readwrite')).put(c));
    },
    chunks: async (session) =>
      request((await store('chunks', 'readonly')).index('session').getAll(session)) as Promise<
        Chunk[]
      >,
    deleteSession: async (session) => {
      const chunks = await store('chunks', 'readwrite');
      const keys = await request(chunks.index('session').getAllKeys(session));
      for (const key of keys) chunks.delete(key);
      await request((await store('sessions', 'readwrite')).delete(session));
    },
  };
}

/** In memory, for tests and browsers without IndexedDB (nothing survives a reload). */
export function memoryStore(): QaStore {
  const sessions = new Map<string, StoredSession>();
  const chunks: Chunk[] = [];
  return {
    putSession: (s) => {
      sessions.set(s.manifest.id, structuredClone(s));
      return Promise.resolve();
    },
    sessions: () => Promise.resolve([...sessions.values()]),
    putChunk: (c) => {
      chunks.push(c);
      return Promise.resolve();
    },
    chunks: (session) => Promise.resolve(chunks.filter((c) => c.session === session)),
    deleteSession: (session) => {
      sessions.delete(session);
      for (let i = chunks.length - 1; i >= 0; i--)
        if (chunks[i]?.session === session) chunks.splice(i, 1);
      return Promise.resolve();
    },
  };
}
